import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/orbit/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/orbit/app/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login({ email, password });
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="orbit-login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A1F44] text-white items-center justify-center p-16">
        <div className="max-w-md">
          <div className="text-[11px] uppercase tracking-[0.22em] text-blue-300 font-semibold">Orbit CRM</div>
          <h1 className="mt-4 text-4xl font-semibold leading-tight">Your revenue engine.<br/>Every account. Every deal.</h1>
          <p className="mt-6 text-white/70">The outbound-first CRM built for revenue teams that ship pipeline weekly.</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-semibold text-[#0A1F44]">Sign in to Orbit</h2>
          <p className="mt-2 text-sm text-slate-500">Welcome back — pick up where you left off.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="orbit-login-form">
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Work email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="orbit-login-email"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Password</label>
                <Link to="/orbit/forgot-password" className="text-xs text-[#1E40AF] hover:underline" data-testid="orbit-forgot-link">
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="orbit-login-password"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]"
                placeholder="Your password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="orbit-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="orbit-login-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#0A1F44] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            Don&apos;t have an account?{" "}
            <Link to="/orbit/register" className="text-[#1E40AF] font-medium hover:underline" data-testid="orbit-register-link">
              Create a workspace
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
